/**
 * Map of AI result sources that can receive user feedback.
 * These values correspond to table names that store AI-generated results
 * and are used in the `ello_ai_result_feedback.result_source` column
 * to identify which AI feature produced the result being reviewed.
 */
export const AI_RESULT_SOURCE_MAP = {
  CALL_OPPORTUNITY_ASSESSMENTS: 'call_opportunity_assessments',
  DOCUMENT_VALIDATION_RUNS: 'document_validation_runs',
  ELLO_ASSISTANT_CHAT_HISTORY: 'ello_assistant_chat_history',
  VEPA_VALIDATION_RESULTS: 'vepa_validation_results',
} as const

export type AiResultSource =
  (typeof AI_RESULT_SOURCE_MAP)[keyof typeof AI_RESULT_SOURCE_MAP]

/**
 * Fixed id of the "Sage Hermes" service-account ello_users row, seeded by
 * migration. Used as reviewed_by when Sage Hermes auto-sets a feedback
 * review state, so reviews render as "Reviewed By: Sage Hermes".
 */
export const SAGE_HERMES_REVIEWER_USER_ID =
  '5a9e0000-0000-4000-8000-000000000001'

export const SAGE_HERMES_REVIEWER_NAME = 'Sage Hermes'

/** Valid langfuse_trace_reviews.review_status values. */
export const FEEDBACK_REVIEW_STATUSES = ['in_review', 'reviewed'] as const
export type FeedbackReviewStatus = (typeof FEEDBACK_REVIEW_STATUSES)[number]

/**
 * sagent triage categories — source of truth for the A–G set. Nothing enforces
 * this at compile time, so any change here MUST be mirrored in the two
 * out-of-band copies:
 *   1. DB CHECK `ello_ai_result_feedback_feedback_category_check` (see
 *      db/migrations/20260706120000_...) — needs a new migration, or the DB
 *      rejects the new value.
 *   2. sage-hermes `skills/sage-ai-feedback-triage/SKILL.md` — the category
 *      definitions the sagent classifies against.
 */
export const FEEDBACK_CATEGORIES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]

/** Categories whose issues page a human at the recurrence threshold. */
export const ACTIONABLE_FEEDBACK_CATEGORIES: readonly FeedbackCategory[] = [
  'B',
  'D',
  'E',
  'G',
]

/**
 * Categories that never page and never carry open issues: sagent records them
 * pre-resolved so they group for visibility without entering the open queue.
 */
export const NON_ACTIONABLE_FEEDBACK_CATEGORIES: readonly FeedbackCategory[] =
  FEEDBACK_CATEGORIES.filter(
    (category) => !ACTIONABLE_FEEDBACK_CATEGORIES.includes(category)
  )

/**
 * Valid ello_ai_result_feedback.issue_status values. Mirror any change in the
 * DB CHECK `ello_ai_result_feedback_issue_status_check` (new migration required).
 */
export const FEEDBACK_ISSUE_STATUSES = ['open', 'resolved'] as const
export type FeedbackIssueStatus = (typeof FEEDBACK_ISSUE_STATUSES)[number]

export const DEFAULT_AI_FEEDBACK_RECURRENCE_THRESHOLD = 3

export const DEFAULT_AI_FEEDBACK_RESOLVED_PREVIEW_COUNT = 3

/** Consolidated global_data value for GlobalDataKeys.AiFeedbackConfig. */
export interface AiFeedbackConfig {
  /** Ello user ids eligible for threshold paging. */
  recipients: string[]
  /** Occurrences before an actionable issue pages a human. */
  recurrenceThreshold: number
  /** Recently-resolved issues kept visible (greyed) per category. */
  resolvedPreviewCount: number
}
